<template lang="pug">
.accordion(:class="[theme, show && 'open']")
  button.a11y.heading(@click="toggle()" :aria-label="__('Toggle Content', 'auto')")
    slot(name="heading")
      .head-slot
        Icon.left-icon(v-if="leftIcon" fill="black" :name="leftIcon" :class="theme === 'rhpa' && 'icon-size-l'")
        component(:is="headingTag") {{ __(heading, 'auto') }}
        .secondary(v-if="secondary") {{ __(secondary, 'auto') }}
    Icon.toggle(
      :class="[theme !== 'rhpa' && 'icon-size-xs']"
      :name="show ? openIcon : closedIcon"
      :fill="theme === 'rhpa' ? 'gray_dark' : 'black' ")
  .content(v-show="show")
    slot(name="content")
    slot
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRogue } from '../../../js/rogue'

const props = withDefaults(defineProps<{
  heading?: string
  secondary?: string | null
  // Deprecated - use :modelValue instead
  open?: boolean
  modelValue?: boolean
  theme?: string
  leftIcon?: string
  closedIcon?: string
  openIcon?: string
  headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  // eslint-disable-next-line vue/prop-name-casing
  'onUpdate:modelValue'?: (value: boolean) => void
}>(), {
  heading    : '',
  secondary  : null,
  open       : false,
  modelValue : false,
  closedIcon : 'plus',
  openIcon   : 'minus',
  headingTag : 'h4',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const rogue = useRogue()

// We have to use a local value because the parent doesn't always pass one in using v-model
const show = ref(props.open || props.modelValue)

watch(() => props.modelValue, (newVal) => {
  show.value = newVal
})

function toggle() {
  if (props['onUpdate:modelValue']) {
    emit('update:modelValue', !show.value)
  } else {
    show.value = !show.value
  }

  if (show.value && rogue) {
    rogue.track.track({ type: 'viewAccordion', name: props.heading })
  }
}
</script>

<style lang="scss" scoped>
button {
  width: 100%;
}
.accordion {
  position: relative;
  border: $border-width-thin solid $color-gray-light;
  border-bottom: none;


   &.custom-rhpa-styles {
    h4 {
      color: rgb(0, 0, 0);
      font-size: 14px;
      font-family: $font-condensed;
      font-weight: bold;
      text-transform: uppercase;
      line-height: 14px;
    }

    .content {
      padding-left: 0;
      padding-top: 0;
    }

    .delete-unit {
      width: 58px;
      height: 12px;
      color: rgb(18, 119, 183);
      font-size: 12px;
      font-family: $font-condensed;
      font-weight: normal;
      text-decoration: underline;
      line-height: 12px;
      cursor: pointer;
      margin-bottom: 5px;
    }
  }

    &.mass-storage-wheels {
      .content {
        padding: 12px 0;
      }

      h4 {
        font-size: 14px;
      }
    }

  .heading {
    display: flex;
    cursor: pointer;
    user-select: none;
    align-items: center;

    * {
      margin-bottom: 0;
    }
  }

  .heading, .content {
    padding: $space-xs $space-s;
    border-bottom: $border-width-thin solid $color-gray-light;
  }

  .head-slot {
    display: flex;
    width: 100%;
    padding-right: 32px;
    h4 {
      flex-grow: 1;
    }
  }

  .icon.toggle {
    position: absolute;
    right: $space-s;
    pointer-events: none;
  }

  .icon.left-icon {
    margin-right: $space-xxs;
  }

  h3 {
    font-size: $size-m;
  }
}

.accordion.rhpa {
  .heading {
    padding: $space-m $space-xs;
    border-bottom: 1px solid $color-gray;
    outline: none;
  }
  &:first-of-type .heading {
    border-top: 1px solid $color-gray;
  }

  .head-slot {
    align-items: center;
  }
  border: none;
}

</style>

<style lang="scss" scoped target="app">
  .platform-ios {
    .accordion .icon {
      top: 14px;
    }
  }
</style>
